import { useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { useBaasInstance } from "../../../../Contexts/BaasInstanceContext";
import { useToast } from "../../../../Hooks/toast";
import { useBaasCapabilities } from "../../../../Hooks/useBaasCapabilities";
import { useBaasPageLoading } from "../../../../Hooks/useBaasPageLoading";
import { useBaasPlatformCreateAccess } from "../../../../Hooks/useBaasPlatformCreateAccess";
import { fetchBaasInstanceScopes } from "../../../../Services/baasApiApi";
import {
  loadBaasExitRailRootAccountSelectOptions,
  loadBaasRootAccountSelectOptions,
} from "../Instances/baasRootAccountOptions";
import { formatInputValue } from "../../../../Utils/formatInputValue";
import {
  fetchBaasInstanceDetail,
  fetchBaasInstanceFeatures,
  getInstanceEfxFees,
  getInstanceEfxLimits,
  getInstanceOverdraftFees,
  getInstancePixFees,
  getInstanceStablesFees,
  getInstanceStablesLimits,
  isBaasHmlInstance,
  resolveBaasCreateParentInstance,
  setBaasInstanceFeatures,
  setBaasInstanceScopes,
  setInstanceEfxFees,
  setInstanceEfxLimits,
  setInstanceOverdraftFees,
  setInstancePixFees,
  setInstanceStablesFees,
  setInstanceStablesLimits,
  updateBaasInstance,
} from "../../../../Services/baasInstancesApi";
import {
  formatBaasTaxDocument,
  resolveBaasInstanceRootDocument,
} from "../../../../Services/baasInstanceRootDocument";
import {
  getInstancePixLimits,
  setInstancePixLimits,
} from "../../../../Services/baasPixApi";
import { BaasSettingsSkeleton } from "../components/BaasSkeleton";
import {
  BAAS_DEFAULT_EFX_FEE_ROWS,
  BAAS_DEFAULT_EFX_LIMIT_ROWS,
  BAAS_DEFAULT_NETWORK_FEE_ROWS,
  BAAS_DEFAULT_PIX_FEE_ROWS,
  BAAS_DEFAULT_PIX_LIMIT_ROWS,
  BAAS_DEFAULT_STABLES_FEE_ROWS,
  BAAS_DEFAULT_STABLES_LIMIT_ROWS,
  BAAS_INSTANCE_FEATURE_OPTIONS,
  BAAS_PUBLISH_SCOPE_CATALOG,
  BAAS_SUPPORTED_COUNTRY_OPTIONS,
  buildDefaultEfxFeeDrafts,
  buildDefaultEfxLimitDrafts,
  buildDefaultFeatureMap,
  buildDefaultNetworkFeeDrafts,
  buildDefaultPixFeeDrafts,
  buildDefaultPixLimitDrafts,
  buildDefaultStablesFeeDrafts,
  buildDefaultStablesLimitDrafts,
  efxFeeDraftsFromPolicies,
  efxFeePoliciesFromDrafts,
  efxLimitPoliciesFromDrafts,
  formatBrlMajorMask,
  hasInstancesOwnerSetScope,
  isGrantableScope,
  mergeScopeCatalog,
  networkFeeDraftsFromPolicies,
  networkFeePoliciesFromDrafts,
  parseBrlMajorMask,
  pixFeeDraftsFromPolicies,
  pixFeePoliciesFromDrafts,
  pixLimitPoliciesFromDrafts,
  countriesFromInstanceDetail,
  resolveCountriesPayload,
  stablesFeeDraftsFromPolicies,
  stablesFeePoliciesFromDrafts,
  stablesLimitDraftKey,
  stablesLimitPoliciesFromDrafts,
  withCardsCoreScopes,
} from "../Instances/baasInstanceCreateConstants";
import { BaasInstanceScopesPicker } from "../Instances/components/BaasInstanceScopesPicker";
import { BaasInstanceRailLimitsPanel } from "../Instances/components/BaasInstanceRailLimitsPanel";
import { BaasHmlChecklistPanel } from "../Instances/components/BaasHmlChecklistPanel";
import { BaasProdAwaitingHomologBanner } from "../Instances/components/BaasProdAwaitingHomologBanner";
import { BaasFundShellsButton } from "../Accounts/components/BaasFundShellsButton";

const inputClass =
  "h-10 w-full rounded-lg border border-gray-200 px-3 text-sm text-brand-dark outline-none transition focus:border-[#e7a73f] disabled:cursor-not-allowed disabled:opacity-60 dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-100";

const panelClass =
  "space-y-4 rounded-xl border border-gray-200 bg-white p-4 dark:border-brand-dark-border dark:bg-brand-dark-surface sm:p-5";

const formatOverdraftFeePct = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return "";
  return formatInputValue(String(Math.max(0, Math.round(n * 100))));
};

const parseOverdraftFeePct = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return 0;
  if (raw.includes(",")) return parseBrlMajorMask(raw) || 0;
  const n = Number(raw.replace(",", "."));
  return Number.isFinite(n) && n >= 0 ? n : 0;
};

const emptyOverdraftFeeDrafts = () => ({
  grantFeePercent: "",
  grantFeeFixed: "",
  interestPercentPerDay: "",
  daysFree: "0",
});

const overdraftFeeDraftsFromResponse = (row) => ({
  grantFeePercent: formatOverdraftFeePct(row?.grant_fee_percent ?? row?.grantFeePercent ?? 0),
  grantFeeFixed: formatBrlMajorMask(row?.grant_fee_fixed ?? row?.grantFeeFixed ?? 0),
  interestPercentPerDay: formatOverdraftFeePct(
    row?.interest_percent_per_day ?? row?.interestPercentPerDay ?? 0,
  ),
  daysFree: String(Math.max(0, Math.trunc(Number(row?.days_free ?? row?.daysFree ?? 0) || 0))),
});

const overdraftFeeBodyFromDrafts = (drafts) => ({
  grant_fee_percent: parseOverdraftFeePct(drafts.grantFeePercent),
  grant_fee_fixed: parseBrlMajorMask(drafts.grantFeeFixed) || 0,
  interest_percent_per_day: parseOverdraftFeePct(drafts.interestPercentPerDay),
  days_free: Math.max(0, Math.trunc(Number(String(drafts.daysFree ?? "0").replace(/\D/g, "")) || 0)),
});

const toggleTrackClass = (on) =>
  `relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition ${
    on ? "bg-[#e7a73f]" : "bg-gray-200 dark:bg-gray-700"
  }`;

const toggleThumbClass = (on) =>
  `pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
    on ? "translate-x-5" : "translate-x-0"
  }`;

const ROOT_CUSTOM = "__custom__";

const TAB_BASICS = "basics";
const TAB_ACCESS = "access";
const TAB_ROOT = "root";
const TAB_SMTP = "smtp";
const TAB_BRANDING = "branding";
const TAB_SCOPES = "scopes";
const TAB_FEATURES = "features";
/** Unified Limites + Tarifas (per-rail) — former separate limits/fees tabs. */
const TAB_FEES = "fees";

const HASH_TO_TAB = {
  "baas-settings-basics": TAB_BASICS,
  "baas-settings-email": TAB_ACCESS,
  "baas-settings-root": TAB_ROOT,
  "baas-settings-smtp": TAB_SMTP,
  "baas-settings-branding": TAB_BRANDING,
  "baas-settings-scopes": TAB_SCOPES,
  "baas-settings-features": TAB_FEATURES,
  "baas-settings-limits": TAB_FEES,
  "baas-settings-pix-limits": TAB_FEES,
  "baas-settings-fees": TAB_FEES,
};

const emptySmtp = () => ({
  host: "smtp.sendgrid.net",
  port: "587",
  username: "apikey",
  password: "",
  fromEmail: "",
  fromName: "",
  hasPassword: false,
  configured: false,
});

const emptyBrandingForm = () => ({
  title: "",
  branchName: "",
  favicon: "",
  logoDefault: "",
  logoWhite: "",
  loginImage: "",
  primaryOriginal: "",
  primaryDark: "",
  primaryDarkest: "",
  primaryLight: "",
  primaryLightest: "",
  siteUrl: "",
  supportUrl: "",
  termsUrl: "",
  privacyUrl: "",
  androidUrl: "",
  iosUrl: "",
  showValutFooter: true,
  publicHost: "",
  dnsAliasesText: "",
  isMatrix: false,
  carteiraWhiteLabel: false,
  isDebug: false,
});

const parseDnsAliasesText = (text) =>
  String(text || "")
    .split(/[\n,]+/)
    .map((host) => host.trim().toLowerCase())
    .filter(Boolean);

const formatDnsAliasesText = (list) =>
  (Array.isArray(list) ? list : [])
    .map((host) => String(host || "").trim())
    .filter(Boolean)
    .join("\n");

const brandingFormFromDetail = (detail) => {
  const branding = detail?.branding && typeof detail.branding === "object" ? detail.branding : {};
  const appConfig =
    detail?.appConfig && typeof detail.appConfig === "object" ? detail.appConfig : {};
  const colors = branding.colors && typeof branding.colors === "object" ? branding.colors : {};
  const primary = colors.primary && typeof colors.primary === "object" ? colors.primary : {};
  const app =
    (appConfig.app && typeof appConfig.app === "object" ? appConfig.app : null) ||
    (branding.app && typeof branding.app === "object" ? branding.app : {}) ||
    {};
  const loginCfg =
    (appConfig.login && typeof appConfig.login === "object" ? appConfig.login : null) ||
    (branding.login && typeof branding.login === "object" ? branding.login : {}) ||
    {};

  return {
    title: branding.title || "",
    branchName: branding.branch_name || branding.branchName || "",
    favicon: branding.favicon || "",
    logoDefault: branding.logo_default || branding.logoDefault || "",
    logoWhite: branding.logo_white || branding.logoWhite || "",
    loginImage: branding.login_image || branding.loginImage || "",
    primaryOriginal: primary.original || "",
    primaryDark: primary.dark || "",
    primaryDarkest: primary.darkest || "",
    primaryLight: primary.light || "",
    primaryLightest: primary.lightest || "",
    siteUrl: appConfig.site_url || appConfig.siteUrl || branding.site || "",
    supportUrl: appConfig.support_url || appConfig.supportUrl || "",
    termsUrl: appConfig.terms_url || appConfig.termsUrl || "",
    privacyUrl: appConfig.privacy_url || appConfig.privacyUrl || "",
    androidUrl: app.android_url || app.androidUrl || "",
    iosUrl: app.ios_url || app.iosUrl || "",
    showValutFooter:
      typeof loginCfg.show_valut_footer === "boolean"
        ? loginCfg.show_valut_footer
        : typeof loginCfg.showValutFooter === "boolean"
          ? loginCfg.showValutFooter
          : true,
    publicHost: detail?.publicHost || "",
    dnsAliasesText: formatDnsAliasesText(detail?.dnsAliases),
    isMatrix: Boolean(detail?.isMatrix),
    carteiraWhiteLabel: Boolean(detail?.carteiraWhiteLabel),
    isDebug: Boolean(detail?.isDebug),
    _colors: colors,
    _branding: branding,
    _appConfig: appConfig,
  };
};

const buildBrandingPatchPayload = (form) => {
  const existingColors =
    form?._colors && typeof form._colors === "object" ? form._colors : {};
  const existingBranding =
    form?._branding && typeof form._branding === "object" ? form._branding : {};
  const existingAppConfig =
    form?._appConfig && typeof form._appConfig === "object" ? form._appConfig : {};
  return {
    branding: {
      ...existingBranding,
      branch_name: String(form.branchName || "").trim(),
      title: String(form.title || "").trim(),
      favicon: String(form.favicon || "").trim(),
      logo_default: String(form.logoDefault || "").trim(),
      logo_white: String(form.logoWhite || "").trim(),
      login_image: String(form.loginImage || "").trim(),
      site: String(form.siteUrl || "").trim(),
      colors: {
        ...existingColors,
        primary: {
          ...(existingColors.primary || {}),
          original: String(form.primaryOriginal || "").trim(),
          dark: String(form.primaryDark || "").trim(),
          darkest: String(form.primaryDarkest || "").trim(),
          light: String(form.primaryLight || "").trim(),
          lightest: String(form.primaryLightest || "").trim(),
        },
      },
      app: {
        ...(existingBranding.app && typeof existingBranding.app === "object"
          ? existingBranding.app
          : {}),
        android_url: String(form.androidUrl || "").trim(),
        ios_url: String(form.iosUrl || "").trim(),
      },
    },
    appConfig: {
      ...existingAppConfig,
      site_url: String(form.siteUrl || "").trim(),
      support_url: String(form.supportUrl || "").trim(),
      terms_url: String(form.termsUrl || "").trim(),
      privacy_url: String(form.privacyUrl || "").trim(),
      app: {
        ...(existingAppConfig.app && typeof existingAppConfig.app === "object"
          ? existingAppConfig.app
          : {}),
        android_url: String(form.androidUrl || "").trim(),
        ios_url: String(form.iosUrl || "").trim(),
      },
      login: {
        ...(existingAppConfig.login && typeof existingAppConfig.login === "object"
          ? existingAppConfig.login
          : {}),
        show_valut_footer: form.showValutFooter !== false,
      },
    },
    publicHost: String(form.publicHost || "").trim() || null,
    dnsAliases: parseDnsAliasesText(form.dnsAliasesText),
  };
};

/** Compact full-width toggle row (label left, switch right) — do not place inside stretched grid cells. */
const ToggleRow = ({ label, hint, checked, onChange, disabled }) => (
  <div className="flex w-full items-center justify-between gap-3 rounded-xl border border-gray-200 px-3 py-2.5 dark:border-brand-dark-border">
    <div className="min-w-0">
      <p className="text-sm font-medium text-brand-dark dark:text-gray-100">{label}</p>
      {hint ? <p className="text-xs text-gray-500 dark:text-gray-400">{hint}</p> : null}
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      className={`${toggleTrackClass(checked)} ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
    >
      <span className={toggleThumbClass(checked)} />
    </button>
  </div>
);

const pixDraftsFromPolicies = (items = []) => {
  const drafts = buildDefaultPixLimitDrafts();
  for (const item of items) {
    const type = Number(item?.type);
    const period = Number(item?.period);
    const amount = Number(item?.amount);
    if (!type || !period || !Number.isFinite(amount) || amount <= 0) continue;
    drafts[`${type}-${period}`] = formatBrlMajorMask(amount);
  }
  return drafts;
};

const centsPoliciesToDrafts = (policies = [], catalogRows = []) => {
  const drafts =
    catalogRows === BAAS_DEFAULT_EFX_LIMIT_ROWS
      ? buildDefaultEfxLimitDrafts()
      : buildDefaultStablesLimitDrafts();
  for (const row of catalogRows) {
    const key = stablesLimitDraftKey(row.operationType, row.periodType);
    const match = (policies || []).find(
      (p) =>
        String(p?.operation_type || p?.operationType || "").toLowerCase() ===
          row.operationType.toLowerCase() &&
        String(p?.period_type || p?.periodType || "hourly").toLowerCase() ===
          row.periodType.toLowerCase(),
    );
    if (!match) continue;
    const cents = Number(match.limit_amount_cents ?? match.limitAmountCents ?? 0);
    if (Number.isFinite(cents) && cents > 0) {
      drafts[key] = formatBrlMajorMask(cents / 100);
    }
  }
  return drafts;
};

const resolveRootChoice = (externalId, options) => {
  const id = String(externalId || "").trim();
  if (!id) return { choice: "", custom: "" };
  if (options.some((option) => option.id === id)) {
    return { choice: id, custom: "" };
  }
  return { choice: ROOT_CUSTOM, custom: id };
};

const tabFromHash = (hash) => {
  const id = String(hash || "").replace(/^#/, "");
  return HASH_TO_TAB[id] || null;
};

const normalizeCurrencyList = (values = []) =>
  [...(Array.isArray(values) ? values : [])].map((item) => String(item).trim().toUpperCase()).sort();

const buildLoadedSnapshot = (detail, countries) => ({
  name: detail.name || "",
  description: detail.description || "",
  isProduction: Boolean(detail.isProduction),
  isDebug: Boolean(detail.isDebug),
  carteiraWhiteLabel: Boolean(detail.carteiraWhiteLabel),
  hmlInstanceId: detail.hmlInstanceId ?? null,
  allowSkipKyc: Boolean(detail.allowSkipKyc),
  inboundPixOriginLockEnabled: Boolean(detail.inboundPixOriginLockEnabled),
  inboundPixOriginDocument: detail.inboundPixOriginDocument || "",
  operatingAllowNegative: Boolean(detail.operatingAllowNegative),
  allowOverBalance: Boolean(detail.allowOverBalance),
  allowedIps: detail.allowedIps || "",
  receiveEmail: false,
  templateId: detail.templateId || "",
  userAccessTemplateId: detail.userAccessTemplateId || "",
  userAccessInviteTemplateId: detail.userAccessInviteTemplateId || "",
  countryCode: countries.countryCode,
  currencies: normalizeCurrencyList(countries.currencies),
  rootAccountId: String(detail.rootAccountId || "").trim(),
});

export const BaasSettingsPage = () => {
  const { addToast } = useToast();
  const { can, isReadOnly } = useBaasCapabilities();
  // Same Valut matrix gate as "Criar instância" / Veriff sync — never IB hasPermission("admin")
  // (AuthContext often defaults Carteira users to admin).
  const {
    isValutConfigAdmin,
    canUseViewAs,
    loading: valutAdminLoading,
    viewAsActive,
  } = useBaasPlatformCreateAccess();
  // Instance console.write OR Valut platform admin (matrix / super_user). Do not OR IB
  // hasPermission("admin"). Valut admins often lack console.write on a *client* card
  // membership — without this OR the whole form (incl. isProduction) stays locked while
  // MS proxy still elevates PATCH to the matrix key.
  // During view-as, isValutConfigAdmin is suppressed — rely on persona caps for general
  // settings, but Valut-only inbound PIX lock still uses canUseViewAs (raw admin).
  const canSaveSettings =
    !isReadOnly && (can("settings.write") || Boolean(isValutConfigAdmin));
  const canEditInboundPixOriginLock = Boolean(canUseViewAs);
  const canToggleInstanceOverBalance = Boolean(canUseViewAs);
  const canEditInstanceName =
    !isReadOnly && (can("settings.write") || Boolean(isValutConfigAdmin) || Boolean(canUseViewAs));
  const fieldsDisabled = !canSaveSettings;
  const inboundPixLockDisabled = !canEditInboundPixOriginLock;
  const { selectedInstance, instances, refreshInstances } = useBaasInstance();
  const pageLoading = useBaasPageLoading(selectedInstance?.id);
  const isLoading = pageLoading || valutAdminLoading;

  const [loadingDetail, setLoadingDetail] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [activeTab, setActiveTab] = useState(TAB_BASICS);

  const [instanceName, setInstanceName] = useState("");
  const [definition, setDefinition] = useState("");
  const [programCode, setProgramCode] = useState("");
  const [isProduction, setIsProduction] = useState(false);
  const [productionRequestedAt, setProductionRequestedAt] = useState(null);
  const [hmlInstanceId, setHmlInstanceId] = useState(null);
  const [supportedCountries, setSupportedCountries] = useState(["BR"]);
  const [allowKycApproval, setAllowKycApproval] = useState(false);
  const [inboundPixOriginLockEnabled, setInboundPixOriginLockEnabled] = useState(false);
  const [inboundPixOriginDocument, setInboundPixOriginDocument] = useState("");
  const [operatingAllowNegative, setOperatingAllowNegative] = useState(false);
  const [allowOverBalance, setAllowOverBalance] = useState(false);
  const [resolvingInboundPixRootDoc, setResolvingInboundPixRootDoc] = useState(false);
  const [receiveEmail, setReceiveEmail] = useState(false);
  const [emailRoutingMode, setEmailRoutingMode] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [userAccessTemplateId, setUserAccessTemplateId] = useState("");
  const [userAccessInviteTemplateId, setUserAccessInviteTemplateId] = useState("");
  const [allowedIps, setAllowedIps] = useState("");
  const [rootAccountChoice, setRootAccountChoice] = useState("");
  const [rootAccountCustom, setRootAccountCustom] = useState("");
  const [generatingVirtualRoot, setGeneratingVirtualRoot] = useState(false);
  const [detailReloadToken, setDetailReloadToken] = useState(0);
  const [busyInstanceActive, setBusyInstanceActive] = useState(false);
  const [registerSmtp, setRegisterSmtp] = useState(false);
  const [clearSmtp, setClearSmtp] = useState(false);
  const [smtp, setSmtp] = useState(emptySmtp);
  const [rootOptions, setRootOptions] = useState([]);
  const [scopeCatalog, setScopeCatalog] = useState(BAAS_PUBLISH_SCOPE_CATALOG);
  const [selectedScopes, setSelectedScopes] = useState(() => [...BAAS_PUBLISH_SCOPE_CATALOG]);
  const [defaultChildScopes, setDefaultChildScopes] = useState([]);
  const [instanceRawScopes, setInstanceRawScopes] = useState([]);
  const [features, setFeatures] = useState(buildDefaultFeatureMap);
  const [pixDrafts, setPixDrafts] = useState(buildDefaultPixLimitDrafts);
  const [stablesDrafts, setStablesDrafts] = useState(buildDefaultStablesLimitDrafts);
  const [efxDrafts, setEfxDrafts] = useState(buildDefaultEfxLimitDrafts);
  const [stablesFeeDrafts, setStablesFeeDrafts] = useState(buildDefaultStablesFeeDrafts);
  const [pixFeeDrafts, setPixFeeDrafts] = useState(buildDefaultPixFeeDrafts);
  const [efxFeeDrafts, setEfxFeeDrafts] = useState(buildDefaultEfxFeeDrafts);
  const [networkFeeDrafts, setNetworkFeeDrafts] = useState(buildDefaultNetworkFeeDrafts);
  const [overdraftFeeDrafts, setOverdraftFeeDrafts] = useState(emptyOverdraftFeeDrafts);
  const [railTab, setRailTab] = useState("pix");
  const [loadedSnapshot, setLoadedSnapshot] = useState(null);
  const [callerRawScopes, setCallerRawScopes] = useState([]);
  const [brandingForm, setBrandingForm] = useState(emptyBrandingForm);

  const parentInstance = useMemo(() => {
    const list = Array.isArray(instances) ? instances : [];
    const matrix = list.find(
      (row) => row?.allowGlobalAccess === true && Number(row?.baasInstanceId) > 0,
    );
    if (matrix) return matrix;
    return resolveBaasCreateParentInstance(
      list,
      selectedInstance?.environment || "production",
    );
  }, [instances, selectedInstance?.environment]);

  /** Baas partner key actually has instances.owner.set (required for PATCH country/KYC/roots). */
  const hasOwnerSetOnApiKey = useMemo(
    () => hasInstancesOwnerSetScope(callerRawScopes),
    [callerRawScopes],
  );

  /**
   * Root/country/liberate UI: Valut matrix admins, or partner keys that literally carry
   * instances.owner.set — never IB hasPermission("admin").
   * Always closed during "Visualizar como" (elevated matrix key must not leak owner edits).
   */
  const canViewOwnerFields = useMemo(
    () =>
      !viewAsActive && (Boolean(isValutConfigAdmin) || hasOwnerSetOnApiKey),
    [viewAsActive, isValutConfigAdmin, hasOwnerSetOnApiKey],
  );

  const canPatchOwnerFields =
    !viewAsActive && (hasOwnerSetOnApiKey || Boolean(isValutConfigAdmin));

  /**
   * Valut platform admin = portal matrix / super_user
   * (`useBaasPlatformCreateAccess.isValutConfigAdmin`). Must NOT use partner-key
   * scopes from GET …/scopes — those describe the API key, not the logged-in person.
   * Client multi-instance create must NOT unlock these tabs.
   */
  const isValutAdmin = Boolean(isValutConfigAdmin);
  const showEfxLimits = Boolean(features.fiat);

  const settingsTabs = useMemo(() => {
    const tabs = [
      { id: TAB_BASICS, labelKey: "baas.settings.tabs.basics" },
      { id: TAB_ACCESS, labelKey: "baas.settings.tabs.access" },
      { id: TAB_SMTP, labelKey: "baas.settings.tabs.smtp" },
      { id: TAB_BRANDING, labelKey: "baas.settings.tabs.branding" },
    ];
    if (canViewOwnerFields) {
      tabs.splice(2, 0, { id: TAB_ROOT, labelKey: "baas.settings.tabs.root" });
    }
    if (isValutAdmin) {
      tabs.push(
        { id: TAB_SCOPES, labelKey: "baas.settings.tabs.scopes" },
        { id: TAB_FEATURES, labelKey: "baas.settings.tabs.features" },
      );
    }
    tabs.push({ id: TAB_FEES, labelKey: "baas.settings.tabs.limits" });
    return tabs;
  }, [isValutAdmin, canViewOwnerFields]);

  useEffect(() => {
    if (!showEfxLimits && railTab === "efx") {
      setRailTab("pix");
    }
  }, [showEfxLimits, railTab]);

  const handleScopePresetEnabled = (preset) => {
    if (preset?.syncFeature === "cards") {
      setFeatures((current) => ({ ...current, cards: true }));
    }
  };

  useEffect(() => {
    const fromHash = tabFromHash(window.location.hash);
    if (!fromHash) return;
    if (!isValutAdmin && [TAB_SCOPES, TAB_FEATURES].includes(fromHash)) {
      setActiveTab(TAB_BASICS);
      return;
    }
    if (!canViewOwnerFields && fromHash === TAB_ROOT) {
      setActiveTab(TAB_BASICS);
      return;
    }
    setActiveTab(fromHash);
    if (window.location.hash.includes("pix-limits")) {
      setRailTab("pix");
    }
  }, [isValutAdmin, canViewOwnerFields, selectedInstance?.id]);

  useEffect(() => {
    if (!parentInstance?.baasInstanceId) {
      setCallerRawScopes([]);
      return undefined;
    }

    let cancelled = false;
    fetchBaasInstanceScopes(parentInstance)
      .then((result) => {
        if (!cancelled) setCallerRawScopes(result?.scopes || []);
      })
      .catch(() => {
        if (!cancelled) setCallerRawScopes([]);
      });

    return () => {
      cancelled = true;
    };
  }, [parentInstance?.baasInstanceId]);

  useEffect(() => {
    if (!settingsTabs.some((tab) => tab.id === activeTab)) {
      setActiveTab(TAB_BASICS);
    }
  }, [settingsTabs, activeTab]);

  useEffect(() => {
    if (!selectedInstance?.baasInstanceId) {
      setLoadingDetail(false);
      return undefined;
    }

    let cancelled = false;
    setLoadingDetail(true);
    setFormError(null);

    const load = async () => {
      setInstanceName(selectedInstance.name || "");
      setProgramCode(selectedInstance.programCode || selectedInstance.programId || "");
      setIsProduction(selectedInstance.isProduction === true);
      try {
        const [
          detail,
          scopesResult,
          featuresList,
          pixLimits,
          stablesLimits,
          efxLimits,
          stablesFees,
          pixFees,
          efxFees,
          overdraftFees,
          rootAccountOptions,
        ] = await Promise.all([
            fetchBaasInstanceDetail(selectedInstance),
            fetchBaasInstanceScopes(selectedInstance),
            fetchBaasInstanceFeatures(selectedInstance).catch(() => []),
            getInstancePixLimits(selectedInstance).catch(() => ({ items: [] })),
            getInstanceStablesLimits(selectedInstance).catch(() => ({ policies: [] })),
            getInstanceEfxLimits(selectedInstance).catch(() => ({ policies: [] })),
            getInstanceStablesFees(selectedInstance).catch(() => ({
              policies: [],
              network_fees: [],
            })),
            getInstancePixFees(selectedInstance).catch(() => ({ policies: [] })),
            getInstanceEfxFees(selectedInstance).catch(() => ({ policies: [] })),
            getInstanceOverdraftFees(selectedInstance).catch(() => ({})),
            (selectedInstance?.allowGlobalAccess
              ? loadBaasExitRailRootAccountSelectOptions(selectedInstance)
              : loadBaasRootAccountSelectOptions(selectedInstance)
            ).catch(() => []),
          ]);

        if (cancelled) return;

        setInstanceName(detail.name || selectedInstance.name || "");
        setDefinition(detail.description || "");
        setProgramCode(detail.programCode || "");
        setIsProduction(Boolean(detail.isProduction));
        setProductionRequestedAt(detail.productionRequestedAt || null);
        setHmlInstanceId(detail.hmlInstanceId);
        setAllowKycApproval(Boolean(detail.allowSkipKyc));
        setInboundPixOriginLockEnabled(Boolean(detail.inboundPixOriginLockEnabled));
        setOperatingAllowNegative(Boolean(detail.operatingAllowNegative));
        setAllowOverBalance(Boolean(detail.allowOverBalance));
        setInboundPixOriginDocument(
          detail.inboundPixOriginDocument
            ? formatBaasTaxDocument(detail.inboundPixOriginDocument)
            : "",
        );
        setAllowedIps(detail.allowedIps || "");
        setReceiveEmail(false);
        setEmailRoutingMode("webhook_only");
        setTemplateId(detail.templateId || "");
        setUserAccessTemplateId(detail.userAccessTemplateId || "");
        setUserAccessInviteTemplateId(detail.userAccessInviteTemplateId || "");

        const resolvedCountries = countriesFromInstanceDetail(detail);
        setSupportedCountries(resolvedCountries);
        setLoadedSnapshot(
          buildLoadedSnapshot(
            { ...detail, name: detail.name || selectedInstance.name || "" },
            resolveCountriesPayload(resolvedCountries),
          ),
        );

        const smtpNext = {
          ...emptySmtp(),
          ...detail.smtp,
          password: "",
        };
        setSmtp(smtpNext);
        setRegisterSmtp(Boolean(detail.smtp?.configured));
        setClearSmtp(false);

        const options = Array.isArray(rootAccountOptions) ? rootAccountOptions : [];
        setRootOptions(options);

        const rootResolved = resolveRootChoice(detail.rootAccountId, options);
        setRootAccountChoice(rootResolved.choice);
        setRootAccountCustom(rootResolved.custom);

        const rawScopes = scopesResult?.scopes || [];
        setInstanceRawScopes(rawScopes);
        const grantable = rawScopes.filter(isGrantableScope);
        const catalog = mergeScopeCatalog(grantable);
        setScopeCatalog(catalog);
        setSelectedScopes(grantable.length > 0 ? grantable : [...BAAS_PUBLISH_SCOPE_CATALOG]);
        const defaults = Array.isArray(detail.defaultPartnerScopes)
          ? detail.defaultPartnerScopes.filter(isGrantableScope)
          : [];
        setDefaultChildScopes(defaults);

        const featureMap = buildDefaultFeatureMap();
        for (const item of featuresList) {
          const code = String(item.featureCode || "").trim();
          if (code && Object.prototype.hasOwnProperty.call(featureMap, code)) {
            featureMap[code] = Boolean(item.enabled);
          }
        }
        setFeatures(featureMap);

        const pixItems = Array.isArray(pixLimits?.items)
          ? pixLimits.items
          : Array.isArray(pixLimits?.policies)
            ? pixLimits.policies
            : [];
        setPixDrafts(pixDraftsFromPolicies(pixItems));

        const stablesPolicies = Array.isArray(stablesLimits?.policies) ? stablesLimits.policies : [];
        setStablesDrafts(centsPoliciesToDrafts(stablesPolicies, BAAS_DEFAULT_STABLES_LIMIT_ROWS));

        const efxPolicies = Array.isArray(efxLimits?.policies) ? efxLimits.policies : [];
        setEfxDrafts(centsPoliciesToDrafts(efxPolicies, BAAS_DEFAULT_EFX_LIMIT_ROWS));

        const feePolicies = Array.isArray(stablesFees?.policies) ? stablesFees.policies : [];
        setStablesFeeDrafts(stablesFeeDraftsFromPolicies(feePolicies));
        const networkFees = Array.isArray(stablesFees?.network_fees)
          ? stablesFees.network_fees
          : Array.isArray(stablesFees?.networkFees)
            ? stablesFees.networkFees
            : [];
        setNetworkFeeDrafts(networkFeeDraftsFromPolicies(networkFees));

        const pixFeePolicies = Array.isArray(pixFees?.policies) ? pixFees.policies : [];
        setPixFeeDrafts(pixFeeDraftsFromPolicies(pixFeePolicies));

        const efxFeePolicies = Array.isArray(efxFees?.policies) ? efxFees.policies : [];
        setEfxFeeDrafts(efxFeeDraftsFromPolicies(efxFeePolicies));
        setOverdraftFeeDrafts(overdraftFeeDraftsFromResponse(overdraftFees));
        setBrandingForm(brandingFormFromDetail(detail));
      } catch (error) {
        if (!cancelled) {
          setInstanceName(selectedInstance.name || "");
          setProgramCode(selectedInstance.programCode || selectedInstance.programId || "");
          setIsProduction(selectedInstance.isProduction === true);
          const code = String(error?.code || error?.error || "").toLowerCase();
          const blocked =
            code === "instance_not_released_for_production"
            || /not liberated for production/i.test(
              String(error?.detail || error?.message || ""),
            );
          setFormError(
            blocked
              ? null
              : error?.message || intl.get("baas.settings.editLoadError"),
          );
        }
      } finally {
        if (!cancelled) setLoadingDetail(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [
    selectedInstance?.id,
    selectedInstance?.baasInstanceId,
    selectedInstance?.name,
    detailReloadToken,
  ]);

  const resolveRootAccountId = () => {
    if (rootAccountChoice === ROOT_CUSTOM) return rootAccountCustom.trim() || "";
    return rootAccountChoice.trim() || "";
  };

  const handleGenerateVirtualRoot = async () => {
    if (!selectedInstance?.baasInstanceId || !isValutAdmin || generatingVirtualRoot) return;
    setGeneratingVirtualRoot(true);
    setFormError(null);
    try {
      await updateBaasInstance({
        instance: selectedInstance,
        useVirtualRoot: true,
      });
      try {
        await refreshInstances();
      } catch {
        // root pin already applied via API
      }
      setDetailReloadToken((token) => token + 1);
      addToast({
        type: "success",
        title: intl.get("baas.settings.generateVirtualRootSuccess"),
      });
    } catch (error) {
      setFormError(
        error?.message || intl.get("baas.instances.createPage.apiReadOnly"),
      );
    } finally {
      setGeneratingVirtualRoot(false);
    }
  };

  const toggleDefaultChildScope = (scope) => {
    setDefaultChildScopes((current) =>
      current.includes(scope) ? current.filter((item) => item !== scope) : [...current, scope],
    );
  };

  const toggleCountry = (code) => {
    setSupportedCountries((current) => {
      if (current.includes(code)) {
        if (current.length === 1) return current;
        return current.filter((item) => item !== code);
      }
      return [...current, code];
    });
  };

  const handleSave = async () => {
    if (
      !canSaveSettings
      && !canEditInboundPixOriginLock
      && !canEditInstanceName
      && !canToggleInstanceOverBalance
    ) {
      addToast({
        type: "error",
        title: intl.get("baas.settings.saveErrorTitle"),
        description: intl.get("baas.settings.saveError"),
      });
      return;
    }
    if (!selectedInstance?.baasInstanceId) {
      addToast({
        type: "error",
        title: intl.get("baas.settings.saveErrorTitle"),
        description: intl.get("baas.settings.editNoInstance"),
      });
      return;
    }

    // View-as suppresses general settings write but Valut admins may still save
    // inbound PIX origin lock only (partners never edit that field).
    if (!canSaveSettings && (canEditInboundPixOriginLock || canEditInstanceName || canToggleInstanceOverBalance)) {
      setSaving(true);
      setFormError(null);
      try {
        const { detail: patchDetail } = await updateBaasInstance({
          instance: selectedInstance,
          name: canEditInstanceName ? instanceName.trim() : undefined,
          inboundPixOriginLockEnabled: canEditInboundPixOriginLock
            ? inboundPixOriginLockEnabled
            : undefined,
          inboundPixOriginDocument: canEditInboundPixOriginLock
            ? inboundPixOriginLockEnabled
              ? String(inboundPixOriginDocument || "").replace(/\D/g, "")
              : ""
            : undefined,
          allowOverBalance: canToggleInstanceOverBalance ? allowOverBalance : undefined,
          operatingAllowNegative: canToggleInstanceOverBalance
            ? operatingAllowNegative
            : undefined,
        });
        if (canToggleInstanceOverBalance && patchDetail) {
          setOperatingAllowNegative(Boolean(patchDetail.operatingAllowNegative));
          setAllowOverBalance(Boolean(patchDetail.allowOverBalance));
        }
        setDetailReloadToken((n) => n + 1);
        await refreshInstances?.();
        addToast({
          type: "success",
          title: intl.get("baas.settings.saveSuccessTitle"),
          description: intl.get("baas.settings.saveSuccess"),
        });
      } catch (error) {
        const isValutAdminApiDeny =
          error?.code === "valut_admin_scope_required"
          || error?.code === "valut_admin_required"
          || error?.code === "valut_admin_key_missing"
          || error?.status === 403;
        setFormError(
          isValutAdminApiDeny
            ? intl.get("baas.accounts.inboundPixOriginLock.adminOnly")
            : error?.detail || error?.message || intl.get("baas.settings.saveError"),
        );
      } finally {
        setSaving(false);
      }
      return;
    }

    if (!instanceName.trim()) {
      setActiveTab(TAB_BASICS);
      setFormError(intl.get("baas.instances.createModal.nameLabel"));
      return;
    }

    if (registerSmtp && !clearSmtp) {
      if (!smtp.fromEmail.trim() || (!smtp.password.trim() && !smtp.hasPassword)) {
        setActiveTab(TAB_SMTP);
        setFormError(intl.get("baas.instances.createModal.smtpRequired"));
        return;
      }
    }

    if (selectedInstance?.allowGlobalAccess) {
      const selectedRoot = rootOptions.find((option) => option.id === resolveRootAccountId());
      if (selectedRoot?.role === "business") {
        setActiveTab(TAB_ROOT);
        setFormError(intl.get("baas.instances.createModal.rootAccountMustBePerson"));
        return;
      }
    }

    if (isValutAdmin) {
      const pixPolicies = pixLimitPoliciesFromDrafts(pixDrafts);
      if (pixPolicies.length !== BAAS_DEFAULT_PIX_LIMIT_ROWS.length * 2) {
        setActiveTab(TAB_FEES);
        setRailTab("pix");
        setFormError(intl.get("baas.instances.createPage.limitsInvalid"));
        return;
      }
      const stablesPolicies = stablesLimitPoliciesFromDrafts(stablesDrafts);
      if (stablesPolicies.length !== BAAS_DEFAULT_STABLES_LIMIT_ROWS.length) {
        setActiveTab(TAB_FEES);
        setRailTab("stables");
        setFormError(intl.get("baas.instances.createPage.stablesLimitsInvalid"));
        return;
      }
      const efxPolicies = efxLimitPoliciesFromDrafts(efxDrafts);
      if (efxPolicies.length !== BAAS_DEFAULT_EFX_LIMIT_ROWS.length) {
        setActiveTab(TAB_FEES);
        setRailTab("efx");
        setFormError(intl.get("baas.instances.createPage.efxLimitsInvalid"));
        return;
      }
      const feePolicies = stablesFeePoliciesFromDrafts(stablesFeeDrafts);
      if (feePolicies.length !== BAAS_DEFAULT_STABLES_FEE_ROWS.length) {
        setActiveTab(TAB_FEES);
        setRailTab("stables");
        setFormError(intl.get("baas.instances.createPage.stablesFeesInvalid"));
        return;
      }
      const networkFees = networkFeePoliciesFromDrafts(networkFeeDrafts);
      if (networkFees.length !== BAAS_DEFAULT_NETWORK_FEE_ROWS.length) {
        setActiveTab(TAB_FEES);
        setRailTab("stables");
        setFormError(intl.get("baas.instances.createPage.networkFeesInvalid"));
        return;
      }
      const pixFeePolicies = pixFeePoliciesFromDrafts(pixFeeDrafts);
      if (pixFeePolicies.length !== BAAS_DEFAULT_PIX_FEE_ROWS.length) {
        setActiveTab(TAB_FEES);
        setRailTab("pix");
        setFormError(intl.get("baas.instances.createPage.pixFeesInvalid"));
        return;
      }
      const efxFeePolicies = efxFeePoliciesFromDrafts(efxFeeDrafts);
      if (efxFeePolicies.length !== BAAS_DEFAULT_EFX_FEE_ROWS.length) {
        setActiveTab(TAB_FEES);
        setRailTab("efx");
        setFormError(intl.get("baas.instances.createPage.efxFeesInvalid"));
        return;
      }
    }

    setSaving(true);
    setFormError(null);

    try {
      // Update existing instance only — never POST /v1/instances (create).
      // Name/access/SMTP → PATCH /v1/instances/{id}
      // Scopes/features/limits/fees → PUT …/{id}/scopes|features|…/limits|fees
      const countriesPayload = resolveCountriesPayload(supportedCountries);
      const patch = {
        instance: selectedInstance,
        name: instanceName.trim(),
        description: definition.trim(),
        allowedIps: allowedIps.trim(),
      };

      // Email routing + SendGrid templates: Valut admin only (instances.owner.set).
      if (isValutAdmin) {
        patch.receiveEmail = false;
        patch.templateId = templateId.trim();
        patch.userAccessTemplateId = userAccessTemplateId.trim();
        patch.userAccessInviteTemplateId = userAccessInviteTemplateId.trim();
      }

      if (clearSmtp) {
        patch.clearSmtp = true;
      } else if (registerSmtp) {
        patch.registerSmtp = true;
        patch.smtp = {
          host: smtp.host,
          port: smtp.port,
          username: smtp.username,
          password: smtp.password,
          fromEmail: smtp.fromEmail,
          fromName: smtp.fromName,
        };
      }

      if (canPatchOwnerFields && loadedSnapshot) {
        const rootId = resolveRootAccountId();
        if (isProduction !== loadedSnapshot.isProduction) patch.isProduction = isProduction;
        if (allowKycApproval !== loadedSnapshot.allowSkipKyc) patch.allowSkipKyc = allowKycApproval;
        patch.countryCode = countriesPayload.countryCode;
        patch.currencies = countriesPayload.currencies;
        if (rootId !== loadedSnapshot.rootAccountId) patch.rootAccountId = rootId;
        // Only request HML twin when flipping into production — not on every fees/limits save.
        if (
          isProduction !== loadedSnapshot.isProduction &&
          isProduction &&
          !hmlInstanceId &&
          !loadedSnapshot.hmlInstanceId
        ) {
          patch.createHmlTwin = true;
        }
      }

      if (canEditInboundPixOriginLock && loadedSnapshot) {
        if (inboundPixOriginLockEnabled !== loadedSnapshot.inboundPixOriginLockEnabled) {
          patch.inboundPixOriginLockEnabled = inboundPixOriginLockEnabled;
        }
        const nextOriginDoc = inboundPixOriginLockEnabled
          ? String(inboundPixOriginDocument || "").replace(/\D/g, "")
          : "";
        const loadedOriginDoc = String(loadedSnapshot.inboundPixOriginDocument || "").replace(
          /\D/g,
          "",
        );
        if (nextOriginDoc !== loadedOriginDoc) {
          patch.inboundPixOriginDocument = nextOriginDoc;
        }
      }

      // Always send booleans (including false) so reload matches DB. Snapshot-diff
      // historically dropped these when GET omitted the keys (Boolean(undefined) === false).
      if (canToggleInstanceOverBalance) {
        patch.operatingAllowNegative = operatingAllowNegative;
        patch.allowOverBalance = allowOverBalance;
      }

      if (isValutAdmin) {
        patch.defaultPartnerScopes = defaultChildScopes;
      }

      if (canSaveSettings) {
        const brandingPayload = buildBrandingPatchPayload(brandingForm);
        patch.branding = brandingPayload.branding;
        patch.appConfig = brandingPayload.appConfig;
        if (canPatchOwnerFields) {
          patch.publicHost = brandingPayload.publicHost;
          patch.dnsAliases = brandingPayload.dnsAliases;
          if (brandingForm.carteiraWhiteLabel !== loadedSnapshot?.carteiraWhiteLabel) {
            patch.carteiraWhiteLabel = Boolean(brandingForm.carteiraWhiteLabel);
          }
          if (brandingForm.isDebug !== loadedSnapshot?.isDebug) {
            patch.isDebug = Boolean(brandingForm.isDebug);
          }
        }
      }

      const { detail: patchDetail } = await updateBaasInstance(patch);
      if (canToggleInstanceOverBalance && patchDetail) {
        // Trust PATCH body (now InstanceDetailResponse) before reload — catches
        // "HTTP 200 but flags dropped from old create-shaped response" regressions.
        setOperatingAllowNegative(Boolean(patchDetail.operatingAllowNegative));
        setAllowOverBalance(Boolean(patchDetail.allowOverBalance));
      }

      if (isValutAdmin) {
        await Promise.all([
          setBaasInstanceScopes(selectedInstance, selectedScopes),
          setBaasInstanceFeatures(
            selectedInstance,
            BAAS_INSTANCE_FEATURE_OPTIONS.map((item) => ({
              feature_code: item.code,
              enabled: Boolean(features[item.code]),
            })),
          ),
          setInstancePixLimits(selectedInstance, {
            policies: pixLimitPoliciesFromDrafts(pixDrafts),
          }),
          setInstanceStablesLimits(
            selectedInstance,
            stablesLimitPoliciesFromDrafts(stablesDrafts),
          ),
          setInstanceEfxLimits(selectedInstance, efxLimitPoliciesFromDrafts(efxDrafts)),
          setInstanceStablesFees(selectedInstance, {
            policies: stablesFeePoliciesFromDrafts(stablesFeeDrafts),
            networkFees: networkFeePoliciesFromDrafts(networkFeeDrafts),
          }),
          setInstancePixFees(selectedInstance, pixFeePoliciesFromDrafts(pixFeeDrafts)),
          setInstanceEfxFees(selectedInstance, efxFeePoliciesFromDrafts(efxFeeDrafts)),
          setInstanceOverdraftFees(
            selectedInstance,
            overdraftFeeBodyFromDrafts(overdraftFeeDrafts),
          ),
        ]);
      } else if (canSaveSettings) {
        await setInstanceOverdraftFees(
          selectedInstance,
          overdraftFeeBodyFromDrafts(overdraftFeeDrafts),
        );
      }

      setLoadedSnapshot(
        buildLoadedSnapshot(
          {
            name: instanceName.trim(),
            description: definition.trim(),
            isProduction,
            hmlInstanceId,
            allowSkipKyc: allowKycApproval,
            inboundPixOriginLockEnabled,
            inboundPixOriginDocument: inboundPixOriginDocument.trim(),
            operatingAllowNegative,
            allowOverBalance,
            allowedIps: allowedIps.trim(),
            receiveEmail,
            templateId: templateId.trim(),
            userAccessTemplateId: userAccessTemplateId.trim(),
            userAccessInviteTemplateId: userAccessInviteTemplateId.trim(),
            rootAccountId: resolveRootAccountId(),
          },
          countriesPayload,
        ),
      );

      try {
        await refreshInstances();
      } catch {
        // local name already patched via API
      }

      setDetailReloadToken((n) => n + 1);

      addToast({
        type: "success",
        title: intl.get("baas.settings.saveSuccess"),
      });
    } catch (error) {
      // Only map real API Valut-admin denials — never invent editClientHint client-side.
      const isValutAdminApiDeny =
        error?.code === "valut_admin_scope_required"
        || error?.code === "valut_admin_required"
        || error?.code === "valut_admin_key_missing"
        || error?.code === "instance_email_access_forbidden"
        || error?.code === "instance_admin_fields_forbidden"
        || error?.code === "instance_is_production_forbidden";
      const message = isValutAdminApiDeny
        ? intl.get("baas.settings.editClientHint")
        : error?.detail ||
          error?.message ||
          intl.get("baas.settings.saveError");
      setFormError(message);
      addToast({
        type: "error",
        title: intl.get("baas.settings.saveErrorTitle"),
        description: message,
      });
    } finally {
      setSaving(false);
    }
  };

  if (isLoading || loadingDetail) {
    return <BaasSettingsSkeleton />;
  }

  if (!selectedInstance?.baasInstanceId) {
    return (
      <div className="baas-settings min-w-0">
        <h1 className="mb-2 text-2xl font-bold text-brand-dark dark:text-gray-100">
          {intl.get("baas.nav.configuracoes")}
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {intl.get("baas.settings.editNoInstance")}
        </p>
      </div>
    );
  }

  const canSaveValutOnlyFlags =
    canEditInboundPixOriginLock || canEditInstanceName || canToggleInstanceOverBalance;
  const showSaveFooter = canSaveSettings || canSaveValutOnlyFlags;
  const inputDisabled = saving || fieldsDisabled;
  const saveDisabled = saving || !(canSaveSettings || canSaveValutOnlyFlags);
  const isProdLaneSelected =
    Boolean(selectedInstance?.baasInstanceId) &&
    !isBaasHmlInstance(selectedInstance);
  const prodInstanceActive = selectedInstance?.active !== false;

  const handleToggleProdInstanceActive = async () => {
    if (!isValutAdmin || !isProdLaneSelected || busyInstanceActive) return;
    const nextActive = !prodInstanceActive;
    if (!nextActive) {
      const confirmed = window.confirm(
        intl.get("baas.settings.deactivateInstanceConfirm"),
      );
      if (!confirmed) return;
    }
    setBusyInstanceActive(true);
    setFormError(null);
    try {
      await updateBaasInstance({
        instance: selectedInstance,
        active: nextActive,
      });
      addToast({
        type: "success",
        title: intl.get(
          nextActive
            ? "baas.settings.activateInstanceSuccess"
            : "baas.settings.deactivateInstanceSuccess",
        ),
      });
      try {
        await refreshInstances();
      } catch {
        // ignore
      }
      setDetailReloadToken((n) => n + 1);
    } catch (err) {
      const message =
        err?.detail ||
        err?.message ||
        intl.get(
          nextActive
            ? "baas.settings.activateInstanceError"
            : "baas.settings.deactivateInstanceError",
        );
      setFormError(message);
    } finally {
      setBusyInstanceActive(false);
    }
  };

  return (
    <div className="baas-settings min-w-0">
      <div className="space-y-6 pb-4">
      <header className="min-w-0">
        <h1 className="text-2xl font-bold text-brand-dark dark:text-gray-100">
          {intl.get("baas.nav.configuracoes")}
          {selectedInstance?.name ? (
            <span className="font-semibold text-gray-400 dark:text-gray-500">
              {" "}
              — {selectedInstance.name}
            </span>
          ) : null}
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {isValutAdmin
            ? intl.get("baas.settings.editSubtitleAdmin")
            : intl.get("baas.settings.editSubtitleClient")}
        </p>
      </header>

      {/* HML gate — client sees checklist; Valut sees Ativar/Desativar (active only). */}
      <BaasHmlChecklistPanel
        instance={selectedInstance}
        mode={isValutAdmin ? "valut" : "client"}
        onActivated={async () => {
          addToast({
            type: "success",
            title: intl.get("baas.instances.hmlChecklist.approveSuccess"),
          });
          try {
            await refreshInstances();
          } catch {
            // ignore
          }
          setDetailReloadToken((n) => n + 1);
        }}
        onDeactivated={async () => {
          addToast({
            type: "success",
            title: intl.get("baas.instances.hmlChecklist.deactivateSuccess"),
          });
          try {
            await refreshInstances();
          } catch {
            // ignore
          }
          setDetailReloadToken((n) => n + 1);
        }}
      />

      {/* Prod-only awaiting + Valut Aprovar produção — banner returns null on HML. */}
      <BaasProdAwaitingHomologBanner
        instance={selectedInstance}
        instances={instances}
        onLiberated={async () => {
          addToast({
            type: "success",
            title: intl.get("baas.homologacao.prodApproveSuccess"),
          });
          try {
            await refreshInstances();
          } catch {
            // ignore
          }
          setDetailReloadToken((n) => n + 1);
        }}
      />

      {/* Valut: Desativar/Ativar on Prod lane (active flag only — not is_production). */}
      {isValutAdmin && isProdLaneSelected ? (
        <section className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-brand-dark-border dark:bg-brand-dark-surface">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-bold text-brand-dark dark:text-gray-100">
                {intl.get("baas.settings.instanceActiveTitle")}
              </h3>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {intl.get("baas.settings.instanceActiveHint")}
              </p>
            </div>
            <button
              type="button"
              disabled={busyInstanceActive}
              onClick={handleToggleProdInstanceActive}
              className={`inline-flex h-9 items-center justify-center rounded-lg px-4 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${
                prodInstanceActive
                  ? "border border-amber-300 bg-white text-amber-900 enabled:hover:bg-amber-50 dark:border-amber-800 dark:bg-transparent dark:text-amber-100 dark:enabled:hover:bg-amber-950/40"
                  : "bg-[#e7a73f] text-white enabled:hover:bg-[#d4962f]"
              }`}
            >
              {busyInstanceActive
                ? intl.get(
                    prodInstanceActive
                      ? "baas.settings.deactivatingInstance"
                      : "baas.settings.activatingInstance",
                  )
                : intl.get(
                    prodInstanceActive
                      ? "baas.settings.deactivateInstance"
                      : "baas.settings.activateInstance",
                  )}
            </button>
          </div>
        </section>
      ) : null}

      {formError ? (
        <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300">
          {formError}
        </p>
      ) : null}

      {fieldsDisabled ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {intl.get("baas.settings.editClientHint")}
        </p>
      ) : !isValutAdmin ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {intl.get("baas.settings.editClientHint")}
        </p>
      ) : null}

      <div
        className="extract-view-tabs extract-view-tabs--inline border-b border-gray-100 dark:border-brand-dark-border"
        role="tablist"
        aria-label={intl.get("baas.settings.tabsLabel")}
      >
        {settingsTabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={`extract-view-tab ${isActive ? "extract-view-tab--active" : ""}`}
              onClick={() => setActiveTab(tab.id)}
              disabled={saving}
            >
              {intl.get(tab.labelKey)}
            </button>
          );
        })}
      </div>

      {activeTab === TAB_BASICS ? (
        <section id="baas-settings-basics" className={panelClass} role="tabpanel">
          <h2 className="dashboard-section-label">
            {intl.get("baas.instances.createPage.sectionBasics")}
          </h2>
          <div>
            <label className="mb-1.5 block text-xs font-medium text-gray-500 dark:text-gray-400">
              {intl.get("baas.instances.createModal.nameLabel")}
            </label>
            <input
              className={inputClass}
              value={instanceName}
              onChange={(event) => setInstanceName(event.target.value)}
              disabled={saving || !canEditInstanceName}
              required
            />
          </div>
          {/* Liberation lives on the Prod catalog card only — never show on HML. */}
          {canPatchOwnerFields && isProdLaneSelected ? (
            <ToggleRow
              label={intl.get("baas.instances.createPage.isProdLabel")}
              hint={intl.get("baas.instances.createPage.isProdHintAdmin")}
              checked={isProduction === true}
              onChange={() => {}}
              disabled
            />
          ) : null}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-gray-500 dark:text-gray-400">
              {intl.get("baas.instances.createPage.definitionLabel")}
            </label>
            <textarea
              className={`${inputClass} h-20 resize-y py-2`}
              value={definition}
              onChange={(event) => setDefinition(event.target.value)}
              disabled={inputDisabled}
              maxLength={1000}
              rows={3}
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-medium text-gray-500 dark:text-gray-400">
              {intl.get("baas.instances.createPage.programCodeLabel")}
            </label>
            <input
              className={`${inputClass} bg-gray-50 dark:bg-brand-dark-elevated/60`}
              value={programCode}
              readOnly
              aria-readonly="true"
            />
          </div>
          {isValutAdmin ? (
            <>
              <div>
                <p className="mb-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
                  {intl.get("baas.instances.createPage.countriesLabel")}
                </p>
                <div className="flex flex-wrap gap-2">
                  {BAAS_SUPPORTED_COUNTRY_OPTIONS.map((item) => {
                    const checked = supportedCountries.includes(item.code);
                    return (
                      <label
                        key={item.code}
                        className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm transition ${
                          checked
                            ? "border-[#e7a73f] bg-[#e7a73f]/10 text-brand-dark dark:text-gray-100"
                            : "border-gray-200 text-gray-600 dark:border-brand-dark-border dark:text-gray-400"
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="h-4 w-4 accent-[#e7a73f]"
                          checked={checked}
                          onChange={() => toggleCountry(item.code)}
                          disabled={inputDisabled || !canPatchOwnerFields}
                        />
                        <span>
                          {intl.get(`baas.instances.createPage.country.${item.code}`)} ({item.currency})
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
              <ToggleRow
                label={intl.get("baas.instances.createPage.allowKycLabel")}
                hint={intl.get("baas.instances.createPage.allowKycHint")}
                checked={allowKycApproval}
                onChange={setAllowKycApproval}
                disabled={inputDisabled || !canPatchOwnerFields}
              />
            </>
          ) : null}
          {canEditInboundPixOriginLock ? (
            <>
              <ToggleRow
                label={intl.get("baas.settings.inboundPixOriginLockLabel")}
                hint={intl.get("baas.settings.inboundPixOriginLockHint")}
                checked={inboundPixOriginLockEnabled}
                onChange={async (nextEnabled) => {
                  if (inboundPixLockDisabled || resolvingInboundPixRootDoc) return;
                  if (!nextEnabled) {
                    setInboundPixOriginLockEnabled(false);
                    setInboundPixOriginDocument("");
                    return;
                  }
                  setInboundPixOriginLockEnabled(true);
                  setResolvingInboundPixRootDoc(true);
                  try {
                    const digits = await resolveBaasInstanceRootDocument(selectedInstance);
                    const formatted = digits ? formatBaasTaxDocument(digits) : "";
                    setInboundPixOriginDocument(formatted);
                    if (!formatted) {
                      setFormError(
                        intl.get("baas.settings.inboundPixOriginRootDocumentMissing"),
                      );
                    } else {
                      setFormError(null);
                    }
                  } catch (error) {
                    setInboundPixOriginDocument("");
                    setFormError(
                      error?.detail
                        || error?.message
                        || intl.get("baas.settings.inboundPixOriginRootDocumentMissing"),
                    );
                  } finally {
                    setResolvingInboundPixRootDoc(false);
                  }
                }}
                disabled={inboundPixLockDisabled || resolvingInboundPixRootDoc}
              />
              {inboundPixOriginLockEnabled ? (
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-gray-500 dark:text-gray-400">
                    {intl.get("baas.settings.inboundPixOriginDocumentLabel")}
                  </label>
                  <input
                    className={`${inputClass} cursor-default bg-gray-50 text-gray-700 dark:bg-brand-dark-surface dark:text-gray-300`}
                    value={resolvingInboundPixRootDoc
                      ? intl.get("baas.settings.inboundPixOriginRootDocumentLoading")
                      : inboundPixOriginDocument}
                    readOnly
                    disabled
                    placeholder={intl.get("baas.settings.inboundPixOriginDocumentPlaceholder")}
                  />
                  <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                    {intl.get("baas.settings.inboundPixOriginDocumentHint")}
                  </p>
                </div>
              ) : null}
            </>
          ) : null}
        </section>
      ) : null}

      {activeTab === TAB_ACCESS ? (
        <section id="baas-settings-email" className={panelClass} role="tabpanel">
          <h2 className="dashboard-section-label">
            {intl.get("baas.instances.createPage.sectionEmail")}
          </h2>
          {isValutAdmin ? (
            <>
              <ToggleRow
                label={intl.get("baas.instances.createModal.receiveEmailLabel")}
                hint={intl.get("baas.instances.createModal.receiveEmailForcedOffHint")}
                checked={false}
                onChange={() => {}}
                disabled
              />
              <div>
                <label className="mb-1.5 block text-xs font-medium text-gray-500 dark:text-gray-400">
                  {intl.get("baas.instances.createModal.templateIdLabel")}
                </label>
                <input
                  className={inputClass}
                  value={templateId}
                  onChange={(event) => setTemplateId(event.target.value)}
                  placeholder={intl.get("baas.instances.createModal.templateIdPlaceholder")}
                  disabled={inputDisabled}
                />
                <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                  {intl.get("baas.instances.createModal.templateIdHint")}
                </p>
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-gray-500 dark:text-gray-400">
                  {intl.get("baas.instances.createModal.userAccessTemplateIdLabel")}
                </label>
                <input
                  className={inputClass}
                  value={userAccessTemplateId}
                  onChange={(event) => setUserAccessTemplateId(event.target.value)}
                  placeholder={intl.get("baas.instances.createModal.userAccessTemplateIdPlaceholder")}
                  disabled={inputDisabled}
                />
                <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                  {intl.get("baas.instances.createModal.userAccessTemplateIdHint")}
                </p>
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-gray-500 dark:text-gray-400">
                  {intl.get("baas.instances.createModal.userAccessInviteTemplateIdLabel")}
                </label>
                <input
                  className={inputClass}
                  value={userAccessInviteTemplateId}
                  onChange={(event) => setUserAccessInviteTemplateId(event.target.value)}
                  placeholder={intl.get(
                    "baas.instances.createModal.userAccessInviteTemplateIdPlaceholder",
                  )}
                  disabled={inputDisabled}
                />
                <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                  {intl.get("baas.instances.createModal.userAccessInviteTemplateIdHint")}
                </p>
              </div>
            </>
          ) : (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {intl.get("baas.settings.accessEmailValutOnlyHint")}
            </p>
          )}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-gray-500 dark:text-gray-400">
              {intl.get("baas.instances.createModal.ipLabel")}
            </label>
            <textarea
              className={`${inputClass} h-20 resize-y py-2`}
              value={allowedIps}
              onChange={(event) => setAllowedIps(event.target.value)}
              placeholder={intl.get("baas.instances.createModal.ipPlaceholder")}
              disabled={inputDisabled}
              rows={3}
            />
          </div>
        </section>
      ) : null}

      {canViewOwnerFields && activeTab === TAB_ROOT ? (
        <section id="baas-settings-root" className={panelClass} role="tabpanel">
          <h2 className="dashboard-section-label">
            {intl.get("baas.settings.rootSectionTitle")}
          </h2>
          <p className="text-xs text-gray-400 dark:text-gray-500">
            {selectedInstance?.allowGlobalAccess
              ? intl.get("baas.instances.createPage.rootHint")
              : intl.get("baas.settings.rootHint")}
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500">
            {intl.get("baas.settings.otcMatrixNote")}
          </p>
          {!canPatchOwnerFields ? (
            <p className="text-xs text-amber-700 dark:text-amber-400">
              {intl.get("baas.instances.createPage.apiReadOnly")}
            </p>
          ) : null}
          <div className="max-w-xl">
            <label className="mb-1.5 block text-xs font-medium text-gray-500 dark:text-gray-400">
              {intl.get("baas.instances.createModal.rootAccountLabel")}
            </label>
            <select
              className={inputClass}
              value={rootAccountChoice}
              onChange={(event) => setRootAccountChoice(event.target.value)}
              disabled={inputDisabled || !canPatchOwnerFields}
            >
              <option value="">{intl.get("baas.instances.createModal.rootAccountNone")}</option>
              {rootOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
              <option value={ROOT_CUSTOM}>
                {intl.get("baas.instances.createModal.rootAccountCustom")}
              </option>
            </select>
            {rootAccountChoice === ROOT_CUSTOM ? (
              <input
                className={`${inputClass} mt-2`}
                value={rootAccountCustom}
                onChange={(event) => setRootAccountCustom(event.target.value)}
                placeholder={intl.get("baas.instances.createModal.rootAccountPlaceholder")}
                disabled={inputDisabled || !canPatchOwnerFields}
              />
            ) : null}
            {selectedInstance?.allowGlobalAccess ? (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {intl.get("baas.instances.createModal.rootAccountMustBePerson")}
              </p>
            ) : null}
          </div>
          {isValutAdmin ? (
            <div className="max-w-xl space-y-2 border-t border-gray-100 pt-4 dark:border-brand-dark-border">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {intl.get("baas.settings.generateVirtualRootHint")}
              </p>
              <button
                type="button"
                className="btn-secondary !h-9 !text-xs"
                disabled={inputDisabled || generatingVirtualRoot}
                onClick={handleGenerateVirtualRoot}
              >
                {generatingVirtualRoot
                  ? intl.get("baas.settings.saving")
                  : intl.get("baas.settings.generateVirtualRoot")}
              </button>
            </div>
          ) : null}
          {features.cards ? (
            <div className="max-w-xl space-y-2 border-t border-gray-100 pt-4 dark:border-brand-dark-border">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {intl.get("baas.settings.fundShellsHint")}
              </p>
              <BaasFundShellsButton className="btn-primary !h-9 !text-xs" />
            </div>
          ) : null}
        </section>
      ) : null}

      {activeTab === TAB_SMTP ? (
        <section id="baas-settings-smtp" className={panelClass} role="tabpanel">
          <h2 className="dashboard-section-label">
            {intl.get("baas.instances.createModal.stepSmtp")}
          </h2>
          {!smtp.configured && !registerSmtp ? (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {intl.get("baas.instances.createModal.registerSmtpHint")}
            </p>
          ) : null}

          <ToggleRow
            label={intl.get("baas.instances.createModal.registerSmtpLabel")}
            hint={intl.get("baas.instances.createModal.registerSmtpHint")}
            checked={registerSmtp}
            onChange={(next) => {
              setRegisterSmtp(next);
              if (next) setClearSmtp(false);
            }}
            disabled={inputDisabled}
          />
          {smtp.configured ? (
            <ToggleRow
              label={intl.get("baas.settings.clearSmtpLabel")}
              hint={intl.get("baas.settings.clearSmtpHint")}
              checked={clearSmtp}
              onChange={(next) => {
                setClearSmtp(next);
                if (next) setRegisterSmtp(false);
              }}
              disabled={inputDisabled}
            />
          ) : null}
          {registerSmtp && !clearSmtp ? (
            <div className="grid gap-4 sm:grid-cols-2">
              {[
                ["host", "smtpHost", "text"],
                ["port", "smtpPort", "text"],
                ["username", "smtpUsername", "text"],
                ["password", "smtpPassword", "password"],
                ["fromEmail", "smtpFromEmail", "email"],
                ["fromName", "smtpFromName", "text"],
              ].map(([field, labelKey, inputType]) => (
                <div key={field}>
                  <label className="mb-1.5 block text-xs font-medium text-gray-500 dark:text-gray-400">
                    {intl.get(`baas.instances.createModal.${labelKey}`)}
                  </label>
                  <input
                    type={inputType}
                    className={inputClass}
                    value={smtp[field]}
                    onChange={(event) =>
                      setSmtp((prev) => ({ ...prev, [field]: event.target.value }))
                    }
                    placeholder={
                      field === "password" && smtp.hasPassword
                        ? intl.get("baas.settings.smtpPasswordKeep")
                        : undefined
                    }
                    autoComplete={field === "password" ? "new-password" : undefined}
                    inputMode={field === "port" ? "numeric" : undefined}
                    required={field === "fromEmail" || field === "password"}
                    disabled={inputDisabled}
                  />
                </div>
              ))}
            </div>
          ) : null}
        </section>
      ) : null}

      {activeTab === TAB_BRANDING ? (
        <section id="baas-settings-branding" className={panelClass} role="tabpanel">
          <h2 className="dashboard-section-label">
            {intl.get("baas.settings.branding.sectionTitle")}
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {intl.get("baas.settings.branding.sectionHint")}
          </p>

          {!brandingForm.publicHost && !brandingForm.isMatrix ? (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-100">
              {intl.get("baas.settings.branding.dnsGoLiveBanner")}
            </p>
          ) : null}

          {brandingForm.publicHost ? (
            <a
              href={`https://${String(brandingForm.publicHost).replace(/^https?:\/\//i, "")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex text-sm font-medium text-[#b79568] underline-offset-2 hover:underline"
            >
              {intl.get("baas.settings.branding.openHost", {
                host: brandingForm.publicHost,
              })}
            </a>
          ) : null}

          <div className="rounded-xl border border-gray-200 p-3 dark:border-brand-dark-border">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              {intl.get("baas.settings.branding.preview")}
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <div className="min-w-0 flex-1">
                <p className="truncate text-base font-semibold text-brand-dark dark:text-gray-100">
                  {brandingForm.title || "—"}
                </p>
                <p className="mt-1 truncate text-xs text-gray-500 dark:text-gray-400">
                  {brandingForm.logoDefault || intl.get("baas.settings.branding.noLogo")}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {[
                  brandingForm.primaryOriginal,
                  brandingForm.primaryDark,
                  brandingForm.primaryLight,
                ]
                  .filter(Boolean)
                  .map((color) => (
                    <span
                      key={color}
                      title={color}
                      className="h-8 w-8 rounded-full border border-gray-200 dark:border-brand-dark-border"
                      style={{ backgroundColor: color }}
                    />
                  ))}
              </div>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            {[
              ["title", "title"],
              ["branchName", "branchName"],
              ["favicon", "favicon"],
              ["logoDefault", "logoDefault"],
              ["logoWhite", "logoWhite"],
              ["loginImage", "loginImage"],
            ].map(([field, labelKey]) => (
              <div key={field} className={field === "loginImage" ? "sm:col-span-2" : undefined}>
                <label className="mb-1.5 block text-xs font-medium text-gray-500 dark:text-gray-400">
                  {intl.get(`baas.settings.branding.${labelKey}`)}
                </label>
                <input
                  className={inputClass}
                  value={brandingForm[field]}
                  onChange={(event) =>
                    setBrandingForm((prev) => ({ ...prev, [field]: event.target.value }))
                  }
                  disabled={inputDisabled}
                />
              </div>
            ))}
          </div>

          <div className="space-y-2">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              {intl.get("baas.settings.branding.loginFlags")}
            </p>
            {!canPatchOwnerFields ? (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {intl.get("baas.settings.branding.loginFlagsOwnerOnly")}
              </p>
            ) : null}
            <ToggleRow
              label={intl.get("baas.settings.branding.carteiraWhiteLabel")}
              hint={intl.get("baas.settings.branding.carteiraWhiteLabelHint")}
              checked={brandingForm.carteiraWhiteLabel === true}
              onChange={(next) =>
                setBrandingForm((prev) => ({ ...prev, carteiraWhiteLabel: next }))
              }
              disabled={inputDisabled || !canPatchOwnerFields}
            />
            <ToggleRow
              label={intl.get("baas.settings.branding.isDebug")}
              hint={intl.get("baas.settings.branding.isDebugHint")}
              checked={brandingForm.isDebug === true}
              onChange={(next) => setBrandingForm((prev) => ({ ...prev, isDebug: next }))}
              disabled={inputDisabled || !canPatchOwnerFields}
            />
            <ToggleRow
              label={intl.get("baas.settings.branding.showValutFooter")}
              hint={intl.get("baas.settings.branding.showValutFooterHint")}
              checked={brandingForm.showValutFooter !== false}
              onChange={(next) =>
                setBrandingForm((prev) => ({ ...prev, showValutFooter: next }))
              }
              disabled={inputDisabled}
            />
          </div>

          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              {intl.get("baas.settings.branding.primaryColors")}
            </p>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
              {[
                ["primaryOriginal", "primaryOriginal"],
                ["primaryDark", "primaryDark"],
                ["primaryDarkest", "primaryDarkest"],
                ["primaryLight", "primaryLight"],
                ["primaryLightest", "primaryLightest"],
              ].map(([field, labelKey]) => (
                <div key={field}>
                  <label className="mb-1.5 block text-xs font-medium text-gray-500 dark:text-gray-400">
                    {intl.get(`baas.settings.branding.${labelKey}`)}
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      className="h-10 w-10 shrink-0 cursor-pointer rounded border border-gray-200 bg-transparent dark:border-brand-dark-border"
                      value={
                        /^#[0-9A-Fa-f]{6}$/.test(String(brandingForm[field] || ""))
                          ? brandingForm[field]
                          : "#b79568"
                      }
                      onChange={(event) =>
                        setBrandingForm((prev) => ({
                          ...prev,
                          [field]: event.target.value,
                        }))
                      }
                      disabled={inputDisabled}
                      aria-label={intl.get(`baas.settings.branding.${labelKey}`)}
                    />
                    <input
                      className={inputClass}
                      value={brandingForm[field]}
                      onChange={(event) =>
                        setBrandingForm((prev) => ({
                          ...prev,
                          [field]: event.target.value,
                        }))
                      }
                      placeholder="#000000"
                      disabled={inputDisabled}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              {intl.get("baas.settings.branding.appLinks")}
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              {[
                ["siteUrl", "siteUrl"],
                ["supportUrl", "supportUrl"],
                ["termsUrl", "termsUrl"],
                ["privacyUrl", "privacyUrl"],
                ["androidUrl", "androidUrl"],
                ["iosUrl", "iosUrl"],
              ].map(([field, labelKey]) => (
                <div key={field}>
                  <label className="mb-1.5 block text-xs font-medium text-gray-500 dark:text-gray-400">
                    {intl.get(`baas.settings.branding.${labelKey}`)}
                  </label>
                  <input
                    className={inputClass}
                    value={brandingForm[field]}
                    onChange={(event) =>
                      setBrandingForm((prev) => ({ ...prev, [field]: event.target.value }))
                    }
                    disabled={inputDisabled}
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              {intl.get("baas.settings.branding.dnsSection")}
            </p>
            {!canPatchOwnerFields ? (
              <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                {intl.get("baas.settings.branding.dnsOwnerOnly")}
              </p>
            ) : null}
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-xs font-medium text-gray-500 dark:text-gray-400">
                  {intl.get("baas.settings.branding.publicHost")}
                </label>
                <input
                  className={inputClass}
                  value={brandingForm.publicHost}
                  onChange={(event) =>
                    setBrandingForm((prev) => ({
                      ...prev,
                      publicHost: event.target.value,
                    }))
                  }
                  placeholder="app.parceiro.com"
                  disabled={inputDisabled || !canPatchOwnerFields}
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-gray-500 dark:text-gray-400">
                  {intl.get("baas.settings.branding.dnsAliases")}
                </label>
                <textarea
                  className={`${inputClass} h-24 resize-y py-2`}
                  value={brandingForm.dnsAliasesText}
                  onChange={(event) =>
                    setBrandingForm((prev) => ({
                      ...prev,
                      dnsAliasesText: event.target.value,
                    }))
                  }
                  placeholder={"hml.parceiro.com\nstaging.parceiro.com"}
                  disabled={inputDisabled || !canPatchOwnerFields}
                  rows={3}
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {intl.get("baas.settings.branding.dnsAliasesHint")}
                </p>
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {isValutAdmin && activeTab === TAB_SCOPES ? (
        <section id="baas-settings-scopes" className={panelClass} role="tabpanel">
          <h2 className="dashboard-section-label">
            {intl.get("baas.instances.createPage.sectionScopes")}
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {intl.get("baas.settings.editScopesHint")}
          </p>
          <BaasInstanceScopesPicker
            catalog={scopeCatalog}
            selected={selectedScopes}
            onChange={setSelectedScopes}
            disabled={inputDisabled}
            onPresetEnabled={handleScopePresetEnabled}
          />

          <div className="mt-8 border-t border-gray-200 pt-6 dark:border-brand-dark-border">
            <h3 className="text-sm font-semibold text-brand-dark dark:text-gray-100">
              Default scopes for child instances
            </h3>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Used when a client creates a new program instance (clone). Admin scopes are never
              included. Empty = copy the parent partner&apos;s grantable scopes.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                className="btn-secondary !h-8 !px-3 !text-xs"
                onClick={() => setDefaultChildScopes([...selectedScopes])}
                disabled={inputDisabled}
              >
                Copy active scopes
              </button>
              <button
                type="button"
                className="btn-secondary !h-8 !px-3 !text-xs"
                onClick={() => setDefaultChildScopes([])}
                disabled={inputDisabled}
              >
                Clear defaults
              </button>
              <span className="self-center text-xs text-gray-500 dark:text-gray-400">
                {defaultChildScopes.length} selected
              </span>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {scopeCatalog.map((scope) => {
                const checked = defaultChildScopes.includes(scope);
                return (
                  <label
                    key={`default-${scope}`}
                    className={`cursor-pointer rounded-lg border px-2.5 py-1.5 text-xs transition ${
                      checked
                        ? "border-emerald-500 bg-emerald-500/10 text-brand-dark dark:text-gray-100"
                        : "border-gray-200 text-gray-600 dark:border-brand-dark-border dark:text-gray-400"
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={checked}
                      onChange={() => toggleDefaultChildScope(scope)}
                      disabled={inputDisabled}
                    />
                    {scope}
                  </label>
                );
              })}
            </div>
          </div>
        </section>
      ) : null}

      {isValutAdmin && activeTab === TAB_FEATURES ? (
        <section id="baas-settings-features" className={panelClass} role="tabpanel">
          <h2 className="dashboard-section-label">
            {intl.get("baas.instances.createPage.sectionPermissions")}
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {intl.get("baas.instances.createPage.permissionsHint")}
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            {BAAS_INSTANCE_FEATURE_OPTIONS.map((item) => {
              const labelKey = `baas.instances.createPage.feature.${item.code}`;
              const hintKey = `baas.instances.createPage.featureHint.${item.code}`;
              const label = intl.get(labelKey);
              const hint = intl.get(hintKey);
              return (
              <ToggleRow
                key={item.code}
                label={label && label !== labelKey ? label : item.code}
                hint={hint && hint !== hintKey ? hint : ""}
                checked={Boolean(features[item.code])}
                onChange={(next) => {
                  setFeatures((current) => ({ ...current, [item.code]: next }));
                  if (item.code === "cards" && next) {
                    setSelectedScopes((current) =>
                      withCardsCoreScopes(current, scopeCatalog),
                    );
                  }
                }}
                disabled={inputDisabled}
              />
              );
            })}
          </div>
          {features.cards ? (
            <p className="rounded-lg border border-[#e7a73f]/40 bg-[#e7a73f]/10 px-3 py-2 text-xs text-brand-dark dark:text-gray-100">
              {intl.get("baas.instances.createPage.cardsIncludes")}
            </p>
          ) : null}
        </section>
      ) : null}

      {activeTab === TAB_FEES ? (
        <section id="baas-settings-fees" className={panelClass} role="tabpanel">
          <h2 id="baas-settings-limits" className="dashboard-section-label">
            {intl.get("baas.settings.tabs.limits")}
          </h2>
          <div className="max-w-xl space-y-3">
            <ToggleRow
              label={intl.get("baas.settings.allowOverBalanceLabel")}
              hint={
                canToggleInstanceOverBalance
                  ? intl.get("baas.settings.allowOverBalanceHint")
                  : intl.get("baas.settings.allowOverBalanceValutOnly")
              }
              checked={allowOverBalance}
              onChange={setAllowOverBalance}
              disabled={saving || !canToggleInstanceOverBalance}
            />
            {canToggleInstanceOverBalance ? (
              <ToggleRow
                label={intl.get("baas.settings.operatingAllowNegativeLabel")}
                hint={intl.get("baas.settings.operatingAllowNegativeHint")}
                checked={operatingAllowNegative}
                onChange={setOperatingAllowNegative}
                disabled={saving || !canToggleInstanceOverBalance}
              />
            ) : null}
            <div className="space-y-3 rounded-xl border border-gray-200 bg-gray-50/60 p-3 dark:border-brand-dark-border dark:bg-brand-dark-elevated/40">
              <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
                {intl.get("baas.settings.overdraftFeesTitle")}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {intl.get("baas.settings.overdraftFeesHint")}
              </p>
              <div className="grid gap-2 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-[11px] font-medium text-gray-500 dark:text-gray-400">
                    {intl.get("baas.settings.overdraftGrantFeePercent")}
                  </label>
                  <div className="relative">
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                      %
                    </span>
                    <input
                      className={`${inputClass} pl-9`}
                      inputMode="decimal"
                      value={overdraftFeeDrafts.grantFeePercent}
                      onChange={(event) =>
                        setOverdraftFeeDrafts((prev) => ({
                          ...prev,
                          grantFeePercent: formatInputValue(String(event.target.value ?? "")),
                        }))
                      }
                      disabled={inputDisabled}
                      aria-label={intl.get("baas.settings.overdraftGrantFeePercent")}
                    />
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-medium text-gray-500 dark:text-gray-400">
                    {intl.get("baas.settings.overdraftGrantFeeFixed")}
                  </label>
                  <div className="relative">
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                      R$
                    </span>
                    <input
                      className={`${inputClass} pl-9`}
                      inputMode="decimal"
                      value={overdraftFeeDrafts.grantFeeFixed}
                      onChange={(event) =>
                        setOverdraftFeeDrafts((prev) => ({
                          ...prev,
                          grantFeeFixed: formatInputValue(String(event.target.value ?? "")),
                        }))
                      }
                      disabled={inputDisabled}
                      aria-label={intl.get("baas.settings.overdraftGrantFeeFixed")}
                    />
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-medium text-gray-500 dark:text-gray-400">
                    {intl.get("baas.settings.overdraftInterestPercent")}
                  </label>
                  <div className="relative">
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                      %
                    </span>
                    <input
                      className={`${inputClass} pl-9`}
                      inputMode="decimal"
                      value={overdraftFeeDrafts.interestPercentPerDay}
                      onChange={(event) =>
                        setOverdraftFeeDrafts((prev) => ({
                          ...prev,
                          interestPercentPerDay: formatInputValue(String(event.target.value ?? "")),
                        }))
                      }
                      disabled={inputDisabled}
                      aria-label={intl.get("baas.settings.overdraftInterestPercent")}
                    />
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-medium text-gray-500 dark:text-gray-400">
                    {intl.get("baas.settings.overdraftDaysFree")}
                  </label>
                  <input
                    className={inputClass}
                    inputMode="numeric"
                    value={overdraftFeeDrafts.daysFree}
                    onChange={(event) =>
                      setOverdraftFeeDrafts((prev) => ({
                        ...prev,
                        daysFree: String(event.target.value ?? "").replace(/\D/g, ""),
                      }))
                    }
                    disabled={inputDisabled}
                    aria-label={intl.get("baas.settings.overdraftDaysFree")}
                  />
                </div>
              </div>
            </div>
          </div>
          <BaasInstanceRailLimitsPanel
            railTab={railTab}
            onRailTabChange={setRailTab}
            pixDrafts={pixDrafts}
            setPixDrafts={setPixDrafts}
            stablesDrafts={stablesDrafts}
            setStablesDrafts={setStablesDrafts}
            efxDrafts={efxDrafts}
            setEfxDrafts={setEfxDrafts}
            pixFeeDrafts={pixFeeDrafts}
            setPixFeeDrafts={setPixFeeDrafts}
            stablesFeeDrafts={stablesFeeDrafts}
            setStablesFeeDrafts={setStablesFeeDrafts}
            efxFeeDrafts={efxFeeDrafts}
            setEfxFeeDrafts={setEfxFeeDrafts}
            networkFeeDrafts={networkFeeDrafts}
            setNetworkFeeDrafts={setNetworkFeeDrafts}
            disabled={inputDisabled || !isValutAdmin}
            showEfx={showEfxLimits}
            instanceName={instanceName || selectedInstance?.displayName || selectedInstance?.name}
          />
        </section>
      ) : null}
      {showSaveFooter ? (
        <div className="flex flex-col gap-3 border-t border-gray-200 bg-white pt-4 dark:border-brand-dark-border dark:bg-transparent sm:flex-row sm:items-center sm:justify-end">
          <p className="text-xs text-gray-500 dark:text-gray-400 sm:mr-auto">
            {intl.get("baas.settings.saveFooterHint")}
          </p>
          <button
            type="button"
            className="btn-primary shrink-0 !h-10 !px-5"
            onClick={handleSave}
            disabled={saveDisabled}
          >
            {saving ? intl.get("baas.settings.saving") : intl.get("baas.settings.save")}
          </button>
        </div>
      ) : null}
      </div>
    </div>
  );
};
